import React, { useState } from "react"
import { useDispatch } from "react-redux"
import { makeStyles } from "@material-ui/core/styles"
import { get } from "lodash"
import moment from "moment"
import CloseIcon from "@material-ui/icons/Close"
import { galleryDetailedList } from "../v2_actions/gallery"
import { getUserStoredCountry } from "../v2_actions/auth_country"

export const MobileGalleryDetailModal = ({ children }) => {
    const [gallery, setGallery] = useState(null)
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const classes = useStyles()
    const dispatch = useDispatch()

    const onGalleryClick = (galleryId) => {
        setLoading(true)
        setOpen(true)
        dispatch(galleryDetailedList.get({ galleryRef: galleryId })).then((result) => {
            setGallery(result)
            setLoading(false)
        })
    }

    const onClose = () => {
        setOpen(false)
        setGallery(null)
    }

    const currencyName = get(getUserStoredCountry(), "currency_name")

    const formatCoord = (value) => Number.parseFloat(value).toFixed(2)

    const renderLocation = () => {
        if (!gallery || gallery.lat === null || gallery.lng === null) return ""
        const lat = gallery.lat > 0
            ? `${formatCoord(gallery.lat)}E`
            : `${formatCoord(Math.abs(gallery.lat))}W`
        const lng = gallery.lng > 0
            ? `${formatCoord(gallery.lng)}N`
            : `${formatCoord(Math.abs(gallery.lng))}S`
        return `${lat} ${lng}`
    }

    const frStatus = () => {
        if (!get(gallery, "can_index_for_facial_recognition")) return "Not active"
        const indexed = get(gallery, "number_of_gallery_photographs_indexed_for_facial_recognition", 0)
        const total = get(gallery, "number_of_photos_in_gallery", 0)
        return `${indexed} of ${total}`
    }

    const renderRow = (label, value) => (
        <div className={classes.detailRow}>
            <span className={classes.detailLabel}>{label}</span>
            <span className={classes.detailValue}>{value}</span>
        </div>
    )

    return (
        <>
            {children({ onGalleryClick })}
            {open && (
                <div className={classes.overlay} onClick={onClose}>
                    <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.modalHeader}>
                            <span className={classes.modalTitle}>
                                {loading ? "Loading..." : get(gallery, "session_name", "")}
                            </span>
                            <CloseIcon className={classes.closeIcon} onClick={onClose} />
                        </div>
                        {!loading && gallery && (
                            <div className={classes.modalBody}>
                                <div className={classes.sectionTitle}>Gallery Details</div>
                                {renderRow("Name", get(gallery, "session_name"))}
                                {renderRow("Date", moment.utc(get(gallery, "shot_at_datetime")).format("Do MMM 'YY"))}
                                {renderRow("Time", moment.utc(get(gallery, "shot_at_datetime")).format("HH:mm"))}
                                {renderRow("Activity", get(gallery, "activity_tag_names", []).join(", "))}
                                {renderRow("Location", renderLocation())}
                                {renderRow("Processed", `${get(gallery, "number_of_photos_in_gallery", 0)} of ${get(gallery, "number_of_photos_in_gallery", 0)}`)}

                                <div className={classes.sectionTitle}>Event Details</div>
                                {renderRow("Event", get(gallery, "gallery_event_name", ""))}
                                {renderRow("Album", get(gallery, "album_name", ""))}
                                {renderRow("Facial Recognition Status", frStatus())}

                                <div className={classes.sectionTitle}>Pricing Details</div>
                                {renderRow("Event Commission Split", `${get(gallery, ["gallery_price", "event_commission_percentage"], 0)} %`)}
                                <div className={classes.priceLabel}>Price:</div>
                                {get(gallery, ["gallery_price", "low_res_price"]) !== null &&
                                    renderRow("Low Res Digital", `${get(gallery, ["gallery_price", "low_res_price"])} ${currencyName || ""}`)
                                }
                                {renderRow("High Res Digital", `${get(gallery, ["gallery_price", "base_price"], "")} ${currencyName || ""}`)}
                                {renderRow("Print Only License", `${get(gallery, ["gallery_price", "base_price"], "")} ${currencyName || ""}`)}

                                <div className={classes.priceLabel}>Discounts:</div>
                                {renderRow("Photos 2-5", get(gallery, ["gallery_price", "discount1_percentage"]))}
                                {renderRow("Photos 6-10", get(gallery, ["gallery_price", "discount2_percentage"]))}
                                {renderRow("Photos 11-50", get(gallery, ["gallery_price", "discount3_percentage"]))}
                                {renderRow("Photos 50+", get(gallery, ["gallery_price", "discount4_percentage"]))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

const useStyles = makeStyles({
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
    },
    modal: {
        backgroundColor: "#fff",
        borderRadius: "16px",
        width: "100%",
        maxWidth: "400px",
        maxHeight: "85vh",
        overflow: "auto",
    },
    modalHeader: {
        background: "linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)",
        borderRadius: "16px 16px 0 0",
        padding: "14px 16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    modalTitle: {
        color: "#fff",
        fontSize: "14px",
        fontWeight: 600,
        flex: 1,
    },
    closeIcon: {
        color: "#fff",
        fontSize: "22px",
        cursor: "pointer",
    },
    modalBody: {
        padding: "16px",
    },
    sectionTitle: {
        fontSize: "14px",
        fontWeight: 700,
        color: "#333",
        borderBottom: "1px solid #e0e0e0",
        paddingBottom: "6px",
        marginBottom: "8px",
        marginTop: "16px",
        "&:first-child": {
            marginTop: 0,
        },
    },
    detailRow: {
        display: "flex",
        justifyContent: "space-between",
        padding: "4px 0",
    },
    detailLabel: {
        fontSize: "13px",
        color: "#666",
        minWidth: "100px",
        flexShrink: 0,
    },
    detailValue: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#333",
        flex: 1,
        textAlign: "right",
    },
    priceLabel: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#333",
        marginTop: "8px",
        marginBottom: "4px",
    },
})
